/* eslint-disable array-callback-return */
import { createContext, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import uploadRepository from "../repositories/UploadRepository";
import { useBoolean, useToast } from '@chakra-ui/react'
import ROUTES from "../constants/Routes";
import { useEffect } from "react";
import { useRef } from "react";
import { AuthContext } from "./AuthProvider";
import { ClientType } from "../utils/clientTypeUtils";

export const UploadContext = createContext({
  // State
  isSubmitting: false,
  uploadedCharts: [],
  apiError: {},
  uploadedChartsRef: { current: [] },

  complaincePlanCharts: [],
  complainacePlanUploadedCharts: null,
  specialtiesProviderMap: [],
  refreshSubmitModalMetadataRef: { current: null },

  isLoadingGetCharts: false,
  isGrouped: false,
  isvalidatingBatchName: false,

  clientProviderTree: {},
  isFetchingClientProviderTree: false,
  specialtyProviderTree: {},
  providerTree: {},

  // Setters
  setUploadedCharts: () => {},
  setComplainacePlanUploadedCharts: () => {},

  // useBoolean controls
  igState: () => {},

  // Actions
  uploadCharts: async () => {},
  submitUploadedCharts: async () => {},
  deleteChart: async () => {},
  bulkDeleteChartUploads: async () => {},

  uploadComplaincePlanCharts: async () => {},
  submitUploadedComplaincePlanCharts: async () => {},
  getUploadedComplaincePlanCharts: async () => {},
  deleteComplaincePlanCharts: async () => {},

  validateBatchName: async () => {},

});

export const UPLOAD_API = {
    CHART_UPLOAD: 'chart_upload',
    SUBMIT_CHART: 'submit_chart',
    DELETE_CHART: 'delete_chart'
};

const UploadProvider = props => {
    const toast = useToast()
    const navigate = useNavigate();
    let newCharts = []
    const [isSubmitting, isState] = useBoolean(false)
    const [apiError, setApiError] = useState({
        [UPLOAD_API.CHART_UPLOAD]: null,
        [UPLOAD_API.SUBMIT_CHART]: null,
        [UPLOAD_API.DELETE_CHART]: null,

    })
    const [uploadedCharts, setUploadedCharts] = useState([])
    const [complaincePlanCharts, setComplaincePlanCharts] = useState([]);
    const [complainacePlanUploadedCharts, setComplainacePlanUploadedCharts] = useState(null);
    const [isLoadingGetCharts, setIsLoadingGetCharts] = useBoolean(false);
    const [isGrouped, igState] = useBoolean(false);
    const [specialtiesProviderMap, setSpecialtiesProvidersMap] = useState([]);
    const uploadedChartsRef = useRef(uploadedCharts);
    const refreshSubmitModalMetadataRef = useRef(null);
    const [isvalidatingBatchName, ivbnState] = useBoolean(false);
    const [clientProviderTree, setClientProviderTree] = useState({});
    const [isFetchingClientProviderTree, ifcptState] = useBoolean(true);
    const [specialtyProviderTree, setSpecialtyProviderTree] = useState({})
    const [providerTree, setProviderTree] = useState({});

    const authContext = useContext(AuthContext);
    const loggedInUser = useMemo(() => authContext?.currentUser, [authContext?.currentUser])

    useEffect(() => {
        getClientProviderTree();
    }, [authContext.clientTypeIdObject])

    useEffect(() => {
        uploadedChartsRef.current = uploadedCharts;
    }, [uploadedCharts]);

    const handleProviderTreeResponse = (response) => {
        const userType = loggedInUser?.user_type;
        switch (loggedInUser?.user_type) {
            case ClientType.HEALTH_SYSTEM:
                setClientProviderTree(response[loggedInUser?.health_system_id?.id]);
                break;
            case ClientType.PHYSICIANS_GROUP:
                setSpecialtyProviderTree(response[loggedInUser?.physicians_group_id?.id].hospitals[loggedInUser.hospital_id?.id])
                break;
            case ClientType.HOSPITAL:
                setSpecialtyProviderTree(response[loggedInUser?.hospital_id?.id]);
                break;

            case ClientType.DEPARTMENT:
                setProviderTree(response);
                break;
            
            case ClientType.PROVIDER:
                break;

            default:
                return;

        }
    }

    const getClientProviderTree = async () => {
        try {
            ifcptState.on();
            const response = await uploadRepository.getClientProviderTree(authContext.clientTypeIdObject);            
            handleProviderTreeResponse(response);
        } catch (err) {
            toast({
                title: 'Fetching specialties-providers map',
                description: `${err.toString()}`,
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top-right'
            })
        } finally {
            ifcptState.off();
        }
    }
    const getClientSpecialtiesProvidersMap = async () => {
        try {
            const specs = await uploadRepository.getClientSpecialtiesProvidersMap(authContext.clientTypeIdObject);
            let newSpecs = {...specs}
            if ('specialties' in specs) {
                newSpecs = {}
                for (const spec of specs.specialties) {
                    newSpecs[spec.id] = {
                        name: spec.name,
                        providers: [{
                            id: specs.id,
                            name: specs.name
                        }]
                    }
                }
            }
            setSpecialtiesProvidersMap(newSpecs);
        } catch (e) {
            toast({
                title: 'Fetching specialties-providers map',
                description: `${e.toString()}`,
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top-right'
            })
        } finally {

        }
    }
    const uploadCharts = async (files, index) => {
        const formData = new FormData()
        if (index === 0) {
            igState.off();
            refreshSubmitModalMetadataRef.current();
            bulkDeleteChartUploads(uploadedCharts.map(chart => chart.id));
            newCharts = files.map(f => {
                return {
                    name: f.name,
                    size: `${Math.floor(f.size / 1000)}kb`,
                    value: 0
                }
            })
        }
        
        formData.append('upload_chart', files[index])
        //upload the chart and set the uploadedChart
        const uploadProgress = (data) => {
            newCharts[index] = { ...newCharts[index], value: Math.round((data.loaded / data.total) * 100) }
            setUploadedCharts([...newCharts])
        }

        if (files.length > index) {
            let response
            try {
                response = await uploadRepository.uploadCharts(formData, uploadProgress)
                if (response.status === 201) {
                    newCharts[index] = {
                        ...newCharts[index],
                        id: response.data.file_obj.id,
                        preview_url: response.data.file_obj.preview_url,
                        uploaded: true,
                        cancelled: false,
                        value: 100
                    }
                    setUploadedCharts(newCharts)
                }
            } catch (e) {
                setApiError({ [UPLOAD_API.CHART_UPLOAD]: "error message" })
                toast({
                    title: 'Chart Upload',
                    description: e.response.data.message ? `${e.response.data.message}` : `${e.toString()}`,
                    status: 'error',
                    duration: 9000,
                    isClosable: true,
                    position: 'top-right'
                })
            } finally {
                uploadCharts(files, index + 1)
            }
        }
    }

    const submitUploadedCharts = async (fileIds = undefined, selectedHospitalId = undefined, selectedSpecId = undefined, selectedProviderId = undefined, batchNotes = undefined, batchName = undefined) => {
        //submit the uploaded chart and redirect to next page
        //submit onlu the none deleted ones
        try {
            isState.on()
            if (!fileIds) {
                fileIds = [];
                uploadedCharts.map(f => {
                    if (!f.cancelled) {
                        fileIds.push(f.id)
                    }
                })
            }
            
            await uploadRepository.submitUploadedCharts({
                'file_ids': fileIds,
                'hospital_id': selectedHospitalId,
                'specialty_id': selectedSpecId,
                'provider_id': selectedProviderId,
                'batch_notes': batchNotes,
                'batch_name': batchName
            })
            setUploadedCharts([])
            uploadedChartsRef.current = [];
            toast({
                title: 'Chart Uploaded',
                description: "",
                status: 'success',
                duration: 9000,
                isClosable: true,
                position: 'top-right'
            })
            navigate(ROUTES.DASHBOARD);

        } catch (e) {
            setApiError({ [UPLOAD_API.SUBMIT_CHART]: "error message" })
            toast({
                title: 'Chart Upload',
                description: `${e.toString()}`,
                status: 'error',
                duration: 9000,
                isClosable: true,
                position: 'top-right'
            })
        } finally {
            isState.off();

        }
    }
    const deleteChart = async (id) => {
        //delete the chart and set uploadedChart data to deleted
        try {
            uploadRepository.deleteChart(id)
        } catch (e) {
            setApiError({ [UPLOAD_API.DELETE_CHART]: "error message" })
            toast({
                title: 'Delete Chart',
                description: `${e.toString()}`,
                status: 'success',
                duration: 9000,
                isClosable: true,
                position: 'top-right'
            })
        } finally {
            // const index = uploadedCharts.findIndex(f => f.id === id)
            // setUploadedCharts(prev => prev.map(chart => chart.id === id ? {...chart, uploaded: false, cancelled: true} : chart))
            // uploadedCharts[index] = { ...uploadedCharts[index], uploaded: false, cancelled: true }
            // setUploadedCharts(preb => prev.filter(chart => chart.id !== id))
        }
    }

    const bulkDeleteChartUploads = async (chartUploadIds) => {
        try {
            uploadRepository.bulkDeleteChartUpload({'chart_upload_ids': chartUploadIds})
        } catch(e) {
            setApiError({ [UPLOAD_API.DELETE_CHART]: "error message" })
            toast({
                title: 'Delete Chart',
                description: `${e.toString()}`,
                status: 'success',
                duration: 9000,
                isClosable: true,
                position: 'top-right'
            });
        } finally {
            setUploadedCharts([]);
        }
    }

    const uploadComplaincePlanCharts = async (files, index) => {
        const formData = new FormData()
        if (index === 0) {
            newCharts = files.map(f => {
                return {
                    name: f.name,
                    size: `${Math.floor(f.size / 1000)}kb`,
                    value: 0
                }
            })
        }
        formData.append('upload_chart', files[index])
        //upload the chart and set the uploadedChart
        const uploadProgress = (data) => {
            newCharts[index] = { ...newCharts[index], value: Math.round((data.loaded / data.total) * 100) }
            setUploadedCharts([...newCharts])
        }

        if (files.length > index) {
            try {
                const response = await uploadRepository.uploadComplaincePlanCharts(formData, uploadProgress)
                if (response.status === 201) {
                    newCharts[index] = {
                        ...newCharts[index],
                        id: response.data.file_obj.id,
                        preview_url: response.data.file_obj.preview_url,
                        uploaded: true,
                        cancelled: false,
                        value: 100
                    }
                    setUploadedCharts([...newCharts])
                }
            } catch (e) {
                setApiError({ [UPLOAD_API.CHART_UPLOAD]: "error message" })
                toast({
                    title: 'Chart Upload',
                    description: `${e.toString()}`,
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                    position: 'top-right'
                })
            } finally {
                uploadCharts(files, index + 1)
            }
        }
    }

    const submitUploadedComplaincePlanCharts = async () => {
        //submit only the none deleted ones
        try {
            isState.on()
            let fileIds = []
            uploadedCharts.map(f => {
                if (!f.cancelled) {
                    fileIds.push(f.id)
                }
            })
            const formData = new FormData()
            formData.append('file_ids', fileIds.toString())
            await uploadRepository.submitUploadedComplaincePlanCharts(formData)
            // setComplaincePlanCharts([...complaincePlanCharts, ...uploadCharts])
            setUploadedCharts([])
            toast({
                title: 'Chart Uploaded',
                description: "",
                status: 'success',
                duration: 5000,
                isClosable: true,
                position: 'top-right'
            })
            // navigate(ROUTES.DASHBOARD);

        } catch (e) {
            setApiError({ [UPLOAD_API.SUBMIT_CHART]: "error message" })
            toast({
                title: 'Chart Upload',
                description: `${e.toString()}`,
                status: 'success',
                duration: 5000,
                isClosable: true,
                position: 'top-right'
            })
        } finally {
            isState.off();

        }
    }

    const getUploadedComplaincePlanCharts = async () => {
        try {
            setIsLoadingGetCharts.on()
            const response = await uploadRepository.getUploadedComplaincePlanCharts();
            setComplaincePlanCharts(response.results);
        } catch (error) {
            toast.showError({
                description: `${error.toString()}`,
              });
        } finally{
            setIsLoadingGetCharts.off()
        }
    }

    const deleteComplaincePlanCharts = async (id) => {
        //delete the chart and set uploadedChart data to deleted
        try {
            await uploadRepository.deleteComplaincePlanCharts(id)

            let dataAfterDelete = complaincePlanCharts.filter((cp) => {
                if (cp.id !== id) return cp;
            })
            setComplaincePlanCharts(dataAfterDelete)
            toast({
                title: 'Success',
                description: `Chart Deleted`,
                status: 'success',
                duration: 5000,
                isClosable: true,
                position: 'top-right'
            })
        } catch (e) {
            setApiError({ [UPLOAD_API.DELETE_CHART]: "error message" })
            toast({
                title: 'Delete Chart',
                description: `${e.toString()}`,
                status: 'success',
                duration: 5000,
                isClosable: true,
                position: 'top-right'
            })
        } finally {
            const index = uploadedCharts.findIndex(f => f.id === id)
            uploadedCharts[index] = { ...uploadedCharts[index], uploaded: false, cancelled: true }
            setUploadedCharts([...uploadedCharts])
        }
    }

    const validateBatchName = async (batchName, suggestion = false) => {
        ivbnState.on();
        try {
            let payload = {batch_name: batchName}
            if (suggestion) {
                payload["suggestion"] = true;
            }
            const response = await uploadRepository.validateBatchName(payload);
            return response;
        } catch(e) {
            toast({
                title: 'Batch Name verification Error',
                description: `${e.toString()}`,
                status: 'success',
                duration: 5000,
                isClosable: true,
                position: 'top-right'
            })
            return {"error": e.detail};
        } finally {
            ivbnState.off();
        }
    }

    const mContext = {
        isSubmitting,
        uploadedCharts,
        apiError,
        uploadedChartsRef,
        uploadCharts,
        submitUploadedCharts,
        deleteChart,
        bulkDeleteChartUploads,
        setUploadedCharts,
        complaincePlanCharts,
        complainacePlanUploadedCharts,
        specialtiesProviderMap,
        refreshSubmitModalMetadataRef,
        setComplainacePlanUploadedCharts,
        uploadComplaincePlanCharts,
        submitUploadedComplaincePlanCharts,
        getUploadedComplaincePlanCharts,
        deleteComplaincePlanCharts,
        isLoadingGetCharts,
        isGrouped,
        igState,
        validateBatchName,
        isvalidatingBatchName,
        clientProviderTree,
        isFetchingClientProviderTree,
        specialtyProviderTree,
        setSpecialtyProviderTree,
        providerTree,
        setProviderTree
    };

    return (
        <UploadContext.Provider value={mContext}>
            {props.children}
        </UploadContext.Provider>
    );
};

export default UploadProvider;
